// Centralized speaker/contact link utilities for transcripts.
// Throws instead of silently falling back.

export const CONTACT_MAP_TOKEN = '---GV_CONTACT_MAP---';

export interface LinkedParticipant {
    contactId: string;
    name: string;
    avatar: string | null;
}

export interface SpeakerLinkMapEntry {
    contactId: string;
    avatar: string | null;
    displayName: string;
}

export type SpeakerLinkMap = Record<string, SpeakerLinkMapEntry>;

// Build a link map from assigned participants and diarized label->name assignments.
export function buildSpeakerLinkMap(
    participants: Array<{ contactId?: string; name: string; avatar?: string | null }>,
    speakerAssignments?: Record<string, string | null | undefined>
): SpeakerLinkMap {
    const map: SpeakerLinkMap = {};
    const add = (key: string, p: { contactId?: string; name: string; avatar?: string | null }) => {
        if (!p.contactId) return; // only link real contacts
        map[key] = { contactId: p.contactId, avatar: p.avatar ?? null, displayName: p.name };
    };
    // Participant names always link to themselves
    for (const p of participants) add(p.name, p);
    // Diarized labels (Speaker 1 etc) link to the assigned participant
    if (speakerAssignments) {
        for (const [label, assignedName] of Object.entries(speakerAssignments)) {
            if (!assignedName) continue;
            const p = participants.find(pp => pp.name === assignedName && !!pp.contactId);
            if (p) add(label, p);
        }
    }
    return map;
}

// Embed link map into text
export function embedSpeakerLinks(transcriptText: string, map: SpeakerLinkMap): string {
    const clean = (transcriptText || '').trimEnd();
    if (!map || Object.keys(map).length === 0) {
        throw new Error('embedSpeakerLinks: empty link map');
    }
    return `${clean}\n\n${CONTACT_MAP_TOKEN}\n${JSON.stringify(map)}`;
}

// Extract transcript text + link map or throw
export function extractSpeakerLinks(raw: string): { transcript: string; map: SpeakerLinkMap } {
    if (!raw) throw new Error('extractSpeakerLinks: empty raw notes');
    const idx = raw.indexOf(CONTACT_MAP_TOKEN);
    if (idx === -1) throw new Error('extractSpeakerLinks: token not found');
    const transcript = raw.substring(0, idx).trimEnd();
    const tokenPart = raw.substring(idx + CONTACT_MAP_TOKEN.length).trim();
    const jsonStart = tokenPart.indexOf('{');
    if (jsonStart === -1) throw new Error('extractSpeakerLinks: malformed token payload');
    let parsed: any;
    try {
        parsed = JSON.parse(tokenPart.substring(jsonStart));
    } catch (e: any) {
        throw new Error(`extractSpeakerLinks: parse failed: ${e?.message || String(e)}`);
    }
    if (!parsed || typeof parsed !== 'object') throw new Error('extractSpeakerLinks: parsed payload invalid');
    return { transcript, map: parsed as SpeakerLinkMap };
}
